import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { DataTableWp } from '@fortune-ui'
import { type WpOrderStatus, type WpOrderType, SUBSCRIPTION_STATUS, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Chip } from '@pets-ui/Chip/Chip'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightSmall, Edit } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { type WordPressMobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { createColumnHelper } from '@tanstack/react-table'
import { wordPressHost } from '@utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v5'
import { CANCEl_SUBSCRIPTION_DIALOG_KEY, REVISE_DELIVERY_AMOUNT_DIALOG_KEY, REVISE_DElIVERY_DATE_DIALOG_KEY } from '../constants/dialogKey'
import { useContextWpMyAccount } from '../useContextWpMyAccount'
import { type OrderListDTO, type RelatedOrderDTO } from '../utils/transformOrderDataDTO'
import { type DialogCancelSubscriptionProps } from './DialogCancelSubscription'
import { type DialogReviseDateProps } from './DialogReviseDate'
import { type DialogReviseDeliveryAmountProps } from './DialogReviseDeliveryAmount'
import { IconTrackingShipping } from './IconTrackingShipping'
import { OrderLabel } from './OrderLabel'

const WORDPRESS_MOBILE: WordPressMobileMediaQuery = '@media (max-width: 960px)'

const styles = stylex.create({
  cancelButton: {
    padding: 0,
    alignSelf: 'flex-end',
    color: colors.blue600,
    fontWeight: 400,
  },
  remindContainer: {
    padding: 8,
    backgroundColor: colors.grey50,
  },
  link: {
    textDecoration: 'none',
    alignItems: 'center',
    color: colors.grey800,
    display: 'flex',
    justifyContent: {
      [WORDPRESS_MOBILE]: 'center',
      default: 'flex-start',
    },
    marginTop: {
      [WORDPRESS_MOBILE]: '16px',
      default: '0px',
    },
  },
})

interface OrderTableProps {
  orderList: OrderListDTO
  status: WpOrderStatus
  type: WpOrderType
  warehouseOrSubscriptionOrderStatus?: WpOrderStatus
}

export function OrderTable({ orderList, status, type, warehouseOrSubscriptionOrderStatus }: OrderTableProps) {
  const { t } = useTranslation()
  const { relatedOrders, fortuneOrderId, nextPaymentAt, remainDeliveryAt, serviceOrderItems, remainQuantity, subscriptionStatus, parentProductId, productId, variationId, periodValue } = orderList
  const isMobile = useResponsive('wordpressMobile')
  const { environment, orderStatus } = useContextWpMyAccount()
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { openDialog: openCancelSubscriptionDialog } = useDialog<DialogCancelSubscriptionProps>(CANCEl_SUBSCRIPTION_DIALOG_KEY)
  const { openDialog: openReviseDeliveryAmountDialog } = useDialog<DialogReviseDeliveryAmountProps>(REVISE_DELIVERY_AMOUNT_DIALOG_KEY)
  const { openDialog: openReviseDateDialog } = useDialog<DialogReviseDateProps>(REVISE_DElIVERY_DATE_DIALOG_KEY)
  const shouldShowAutoRenewContent = orderStatus === WP_ORDER_STATUS.PROCESSING || (orderStatus === WP_ORDER_STATUS.ALL && warehouseOrSubscriptionOrderStatus === WP_ORDER_STATUS.COMPLETED)
  const [chip, setChip] = useState<'pending' | 'completed'>('pending')
  const isPendingRelationOrders = relatedOrders?.filter(order => order.status === WP_ORDER_STATUS.PENDING || order.status === WP_ORDER_STATUS.PROCESSING || order.status === WP_ORDER_STATUS.UNCREATED)
  const isCompletedRelationOrders = relatedOrders?.filter(order => order.status === WP_ORDER_STATUS.COMPLETED)
  const shouldShowChips = status === WP_ORDER_STATUS.PROCESSING || (status === WP_ORDER_STATUS.ALL && warehouseOrSubscriptionOrderStatus === WP_ORDER_STATUS.COMPLETED)
  const displayOrdersWithChips = chip === 'pending' ? isPendingRelationOrders : isCompletedRelationOrders
  const displayOrders = shouldShowChips ? displayOrdersWithChips : relatedOrders
  const shouldShowReviseDeliveryAmount = (status === WP_ORDER_STATUS.PROCESSING || status === WP_ORDER_STATUS.PENDING || status === WP_ORDER_STATUS.ALL) && typeof remainQuantity === 'number' && remainQuantity > 0
  const isWarehouseStatusActive = type === WP_ORDER_TYPE.SERVICE_ORDER && subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
  const isWarehouseStatusCancelled = type === WP_ORDER_TYPE.SERVICE_ORDER && subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED
  const isSubscriptionStatusActive = type === WP_ORDER_TYPE.SUBSCRIBE && subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
  const isSubscriptionStatusCancelled = type === WP_ORDER_TYPE.SUBSCRIBE && subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED
  const relatedOrderCreatedTime = orderList.relatedOrderCreatedTime
  const handleClickChip = (status: typeof chip) => {
    setChip(status)
  }

  const columnHelper = createColumnHelper<RelatedOrderDTO & { action?: string }>()
  const columns = [
    columnHelper.accessor('orderId', {
      header: t('wpOrder.orderId') ?? '訂單編號',
      size: 60,
      cell: ({ row }) => {
        const { orderId } = row.original
        return (
          <Text>{orderId || '-'}</Text>
        )
      },
    }),
    columnHelper.accessor('productName', {
      header: t('wpOrder.productSpecification') ?? '商品規格',
      size: 140,
      cell: ({ row }) => {
        const { productName } = row.original
        return (
          <Text>{productName || '-'}</Text>
        )
      },
    }),
    columnHelper.accessor('quantity', {
      header: t('wpOrder.quantity') ?? '商品數量',
      size: 80,
      cell: ({ row }) => {
        const { quantity } = row.original
        return (
          <Text align="right">{quantity || '-'}</Text>
        )
      },
    }),
    columnHelper.accessor('status', {
      header: t('wpOrder.orderStatus') ?? '訂單狀態',
      size: 80,
      cell: ({ row }) => {
        const { status } = row.original
        return (
          <Text>{t(`wpOrderStatus.${status}`) || '-'}</Text>
        )
      },
    }),
    columnHelper.accessor('shippingLabelProvider', {
      header: t('wpOrder.orderTrackingNumber') ?? '託運單號',
      size: 100,
      cell: ({ row }) => {
        const { shippingLabelProvider, status } = row.original
        // @ts-expect-error - TODO: fix this
        return <IconTrackingShipping status={status} {...shippingLabelProvider[0]} />
      },
    }),
    columnHelper.accessor('deliveryDate', {
      header: t('wpOrder.estimatedDeliveryDate') ?? '預計到貨日',
      size: 120,
      cell: ({ row }) => {
        const { deliveryDate, status, remainDeliveryIndex } = row.original
        const isPendingOrder = status === WP_ORDER_STATUS.PENDING || status === WP_ORDER_STATUS.UNCREATED
        const isCancelledOrder = status === WP_ORDER_STATUS.CANCELLED
        if (isCancelledOrder) {
          return <Text>-</Text>
        }
        return (
          <HStack gap={2} alignItems="center" flexWrap="nowrap">
            <Text>{deliveryDate || '-'}</Text>
            {isPendingOrder
            && (
              <Button
                variant="text"
                color="grey"
                onClick={() => {
                  if (relatedOrderCreatedTime && remainDeliveryAt && serviceOrderItems && fortuneOrderId && remainDeliveryIndex !== undefined && periodValue) {
                    openReviseDateDialog({
                      fortuneOrderId,
                      serviceOrderItems,
                      remainDeliveryAt,
                      remainDeliveryIndex,
                      periodValue,
                    })
                  }
                }}
              >
                <Edit fontSize={20} />
              </Button>
            )}
          </HStack>
        )
      },
    }),
    columnHelper.accessor('action', {
      header: '',
      size: 100,
      cell: ({ row }) => {
        const { orderId } = row.original
        if (!orderId) return null
        if (environment === 'wordPress') {
          return (
            <a {...stylex.props(styles.link)} href={`${wordPressHost}/my-account/view-order/${orderId}/`}>
              {t('wpOrder.viewOrderDetailButton')}
              <ArrowRightSmall />
            </a>
          )
        }

        if (environment === 'merchant') {
          // TODO: 等待 fastpass 更新 API 後再實現
          return (
            <Link {...stylex.props(styles.link)} to="123">
              {t('wpOrder.viewOrderDetailButton')}
              <ArrowRightSmall />
            </Link>
          )
        }
      },
    }),
  ]
  const handleClickCancelSubscription = () => {
    if (!fortuneOrderId) return
    openCancelSubscriptionDialog({ fortuneOrderId })
    sendEvent({
      eventName: 'open_subscription_popup',
      eventParams: {},
    })
  }

  return (
    <>
      <HStack my={2} justifyContent="space-between">
        <HStack gap={2}>
          {shouldShowChips
          && (
            <>
              <Chip selected={chip === 'pending'} onClick={() => handleClickChip('pending')}>
                <Text variant="subtitle">{t('wpOrder.backlogOrder')}</Text>
              </Chip>
              <Chip selected={chip === 'completed'} onClick={() => handleClickChip('completed')}>
                <Text variant="subtitle">{t('wpOrder.completedOrder')}</Text>
              </Chip>
            </>
          )}
        </HStack>
        <Box>
          {shouldShowReviseDeliveryAmount
          && (
            <Button
              variant="text"
              color="blue"
              onClick={() => {
                if (!variationId || !productId || !remainDeliveryAt || !serviceOrderItems || !fortuneOrderId) return
                openReviseDeliveryAmountDialog({
                  productId,
                  variationId,
                  remainDeliveryAt,
                  serviceOrderItems,
                  fortuneOrderId,
                })
              }}
            >
              <Text>{t('wpOrder.reviseDeliveryAmount')}</Text>
            </Button>
          )}
        </Box>
      </HStack>
      {displayOrders?.length === 0
        ? <Box minHeight="56px" display="flex" justifyContent="center" alignItems="center"><Text color={colors.grey600}>{t('wpOrder.noOrder')}</Text></Box>
        : isMobile
          ? (
            <Box>
              {displayOrders?.map((order, index) => {
                const isPendingOrder = order.status === WP_ORDER_STATUS.PENDING || order.status === WP_ORDER_STATUS.UNCREATED
                const isCancelledOrder = order.status === WP_ORDER_STATUS.CANCELLED
                const shouldShowEditDeliveryDate = isPendingOrder && remainDeliveryAt && serviceOrderItems && parentProductId && order.remainDeliveryIndex !== undefined
                const orderDetailHref = `${wordPressHost}/my-account/view-order/${order.orderId}/`
                return (
                  <Box key={index} mb={4}>
                    <VStack gap={2} mb={2}>
                      <OrderLabel label={t('wpOrder.orderId')} value={order.orderId ?? '-'} />
                      <OrderLabel label={t('wpOrder.productSpecification')} value={order.productName} />
                      <OrderLabel label={t('wpOrder.quantity')} value={order.quantity} />
                      <OrderLabel label={t('wpOrder.orderStatus')} value={t(`wpOrderStatus.${order.status}`)} />
                      <OrderLabel
                        label={t('wpOrder.orderTrackingNumber')}
                        value={
                        // @ts-expect-error - TODO: fix this
                          <IconTrackingShipping status={order.status} {...order.shippingLabelProvider[0]} />
                        }
                      />
                      {isCancelledOrder
                        ? <OrderLabel label={t('wpOrder.estimatedDeliveryDate')} value="-" />
                        : (
                          <OrderLabel label={t('wpOrder.estimatedDeliveryDate')} value={order.deliveryDate}>
                            {isPendingOrder
                            && (
                              <Button
                                variant="text"
                                color="grey"
                                onClick={() => {
                                  if (!shouldShowEditDeliveryDate || order.remainDeliveryIndex === undefined || !fortuneOrderId || !relatedOrderCreatedTime || !periodValue) return
                                  openReviseDateDialog({
                                    fortuneOrderId,
                                    serviceOrderItems,
                                    remainDeliveryAt,
                                    remainDeliveryIndex: order.remainDeliveryIndex,
                                    periodValue,
                                  })
                                }}
                              >
                                <Edit fontSize={20} />
                              </Button>
                            )}
                          </OrderLabel>
                        )}
                    </VStack>
                    {order.orderId && (
                      <a {...stylex.props(styles.link)} href={orderDetailHref}>
                        {t('wpOrder.viewOrderDetailButton')}
                        <ArrowRightSmall />
                      </a>
                    )}
                    {index !== displayOrders.length - 1 && <Divider mt={4} />}
                  </Box>
                )
              })}
            </Box>
          )
          : (
            <DataTableWp
              columns={columns}
              data={displayOrders || []}
            />
          )}
      {shouldShowAutoRenewContent
      && (
        <>
          {isWarehouseStatusActive
          && (
            <VStack gap={1} rootStyle={styles.remindContainer}>
              <Text color={colors.grey800}>{t('wpOrder.autoRenewNextPaymentAt', { date: nextPaymentAt })}</Text>
              <Text color={colors.grey500}>{t('wpOrder.autoRenewDescription')}</Text>
              {fortuneOrderId && <Button variant="text" rootStyle={styles.cancelButton} onClick={handleClickCancelSubscription}>{t('wpOrder.cancelAutoRenewButton', { defaultValue: '取消自動購買' })}</Button>}
            </VStack>
          )}
          {isWarehouseStatusCancelled
          && (
            <VStack gap={1} rootStyle={styles.remindContainer}>
              <Text color={colors.grey800}>{t('wpOrder.canceledAutoRenew', { defaultValue: '已取消自動購買' })}</Text>
              <Text color={colors.grey500}>{t('wpOrder.canceledAutoRenewDescription', { defaultValue: '上方所列訂單完成配送後，系統將不會自動再次訂購下一週期寄倉訂單。' })}</Text>
            </VStack>
          )}
        </>
      )}

      {shouldShowAutoRenewContent
      && (
        <>
          {isSubscriptionStatusActive
          && (
            <VStack gap={1} rootStyle={styles.remindContainer}>
              <Text color={colors.grey800}>{t('wpSubscription.autoRenewNextPaymentAt', { date: nextPaymentAt })}</Text>
              <Text color={colors.grey500}>{t('wpSubscription.autoRenewDescription')}</Text>
              {fortuneOrderId && <Button variant="text" rootStyle={styles.cancelButton} onClick={handleClickCancelSubscription}>{t('wpSubscription.cancelAutoRenewButton', { defaultValue: '取消自動購買' })}</Button>}
            </VStack>
          )}
          {isSubscriptionStatusCancelled
          && (
            <VStack gap={1} rootStyle={styles.remindContainer}>
              <Text color={colors.grey800}>{t('wpSubscription.canceledAutoRenew', { defaultValue: '已取消自動購買' })}</Text>
              <Text color={colors.grey500}>{t('wpSubscription.canceledAutoRenewDescription', { defaultValue: '上方所列訂單完成配送後，系統將不會自動再次訂購下一週期訂閱訂單。' })}</Text>
            </VStack>
          )}
        </>
      )}
      <Divider my={3} />
    </>
  )
}
